/**
 * Official XREF01 DiagnoseDescriptionCode and RelationCode values.
 *
 * DiagnoseDescriptionCodeType (15 values) and RelationCodeType:
 * MedCom OIOXML ValueLists.xsd, namespace
 * http://rep.oio.dk/medcom.dk/xml/schemas/2017/05/01/
 *
 * The 2014/10/08 copy in this repo
 * (`supabase/functions/_shared/medcom-xsd/2014/10/08/ValueLists.xsd`
 * DiagnoseDescriptionCodeType, lines 159–171) lists the first nine values.
 * XREF01 VersionCode XH0131R XML Kvalifikatorliste (HospitalReferral Revideret)
 * adds `henv_diagnose_ny_sygdom` and `henv_diagnose_samme_sygdom` (LPR3).
 * The 2017/05/01 shared list adds the four henvisningstype values
 * `speciallaegehenvisning`, `fysioterapihenvisning`, `psykologhenvisning`,
 * `fysioterapihenvisningspgruppe`.
 *
 * Element validity (XREF01 XH0131R XML Facitliste):
 * - Refer (HenvisningsDiagnose): `henv_diagnose`, `henv_diagnose_ny_sygdom`,
 *   `henv_diagnose_samme_sygdom`. The four henvisningstype codes are also
 *   DiagnoseDescriptionCodeType, so they are schema-valid on Refer.
 * - ReferralAdditional (HenvisningsTillægsDiagnose, max 5): documented
 *   `forloebsdiagnose`, `tillaegsdiagnose`, `bidiagnose`, `uspec_diagnose`;
 *   the kvalifikatorliste also lists `aktionsdiagnose`, `midlertidig_diagnose`,
 *   `operation`, `roentgenundersoegelse`. The four henvisningstype codes are
 *   schema-valid here. `henv_*` is Refer-only — MedCom Testcenter rejects
 *   DiagnoseDescriptionCode `henv_diagnose` on ReferralAdditional.
 *
 * RelationCodeType is unchanged from 2014/10/08 ValueLists.xsd:
 * uspec_paaroerende = "Uspecificeret pårørende el. værge."
 * XREF01 has no separate `vaerge` code; a værge is sent as uspec_paaroerende.
 */

export const XREF01_DIAGNOSE_DESCRIPTION_CODES = [
  'uspec_diagnose',
  'henv_diagnose',
  'henv_diagnose_ny_sygdom',
  'henv_diagnose_samme_sygdom',
  'bidiagnose',
  'tillaegsdiagnose',
  'aktionsdiagnose',
  'midlertidig_diagnose',
  'forloebsdiagnose',
  'operation',
  'roentgenundersoegelse',
  'speciallaegehenvisning',
  'fysioterapihenvisning',
  'psykologhenvisning',
  'fysioterapihenvisningspgruppe',
] as const;

export type Xref01DiagnoseDescriptionCode = (typeof XREF01_DIAGNOSE_DESCRIPTION_CODES)[number];

/** UI order: today's default henv_diagnose first, then LPR3, then henvisningstype. */
export const XREF01_REFER_DIAGNOSE_DESCRIPTION_CODES = [
  'henv_diagnose',
  'henv_diagnose_ny_sygdom',
  'henv_diagnose_samme_sygdom',
  'speciallaegehenvisning',
  'fysioterapihenvisning',
  'psykologhenvisning',
  'fysioterapihenvisningspgruppe',
] as const;

export type Xref01ReferDiagnoseDescriptionCode =
  (typeof XREF01_REFER_DIAGNOSE_DESCRIPTION_CODES)[number];

export const XREF01_REFER_DIAGNOSE_DEFAULT: Xref01ReferDiagnoseDescriptionCode = 'henv_diagnose';

/** UI order: default tillaegsdiagnose first, then the rest of the official list minus henv_*. */
export const XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_CODES = [
  'tillaegsdiagnose',
  'forloebsdiagnose',
  'bidiagnose',
  'uspec_diagnose',
  'aktionsdiagnose',
  'midlertidig_diagnose',
  'operation',
  'roentgenundersoegelse',
  'speciallaegehenvisning',
  'fysioterapihenvisning',
  'psykologhenvisning',
  'fysioterapihenvisningspgruppe',
] as const;

export type Xref01AdditionalDiagnoseDescriptionCode =
  (typeof XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_CODES)[number];

export const XREF01_ADDITIONAL_DIAGNOSE_DEFAULT: Xref01AdditionalDiagnoseDescriptionCode =
  'tillaegsdiagnose';

/** Official Danish labels from the XREF01/XDIS01/XREF07 kvalifikatorliste. */
export const XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA: Record<Xref01DiagnoseDescriptionCode, string> = {
  uspec_diagnose: 'Uspecificeret diagnose',
  henv_diagnose: 'Henvisningsdiagnose',
  henv_diagnose_ny_sygdom: 'Henvisningsdiagnose, ny sygdom',
  henv_diagnose_samme_sygdom: 'Henvisningsdiagnose, samme sygdom',
  bidiagnose: 'Bidiagnose',
  tillaegsdiagnose: 'Tillægsdiagnose',
  aktionsdiagnose: 'Aktionsdiagnose',
  midlertidig_diagnose: 'Midlertidig diagnose',
  forloebsdiagnose: 'Forløbsdiagnose',
  operation: 'Operation',
  roentgenundersoegelse: 'Røntgenundersøgelse',
  speciallaegehenvisning: 'Speciallægehenvisning',
  fysioterapihenvisning: 'Fysioterapihenvisning',
  psykologhenvisning: 'Psykologhenvisning',
  fysioterapihenvisningspgruppe: 'Fysioterapihenvisning (gruppe)',
};

export const XREF01_REFER_DIAGNOSE_DESCRIPTION_LABELS_DA: Record<
  Xref01ReferDiagnoseDescriptionCode,
  string
> = {
  henv_diagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.henv_diagnose,
  henv_diagnose_ny_sygdom: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.henv_diagnose_ny_sygdom,
  henv_diagnose_samme_sygdom: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.henv_diagnose_samme_sygdom,
  speciallaegehenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.speciallaegehenvisning,
  fysioterapihenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.fysioterapihenvisning,
  psykologhenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.psykologhenvisning,
  fysioterapihenvisningspgruppe: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.fysioterapihenvisningspgruppe,
};

export const XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_LABELS_DA: Record<
  Xref01AdditionalDiagnoseDescriptionCode,
  string
> = {
  tillaegsdiagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.tillaegsdiagnose,
  forloebsdiagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.forloebsdiagnose,
  bidiagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.bidiagnose,
  uspec_diagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.uspec_diagnose,
  aktionsdiagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.aktionsdiagnose,
  midlertidig_diagnose: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.midlertidig_diagnose,
  operation: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.operation,
  roentgenundersoegelse: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.roentgenundersoegelse,
  speciallaegehenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.speciallaegehenvisning,
  fysioterapihenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.fysioterapihenvisning,
  psykologhenvisning: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.psykologhenvisning,
  fysioterapihenvisningspgruppe: XREF01_DIAGNOSE_DESCRIPTION_LABELS_DA.fysioterapihenvisningspgruppe,
};

export const XREF01_ADDITIONAL_TYPE_LABEL_DA = 'Diagnosetype';
export const XREF01_REFER_TYPE_LABEL_DA = 'Diagnosetype';

export const XREF01_RELATION_CODES = ['uspec_paaroerende', 'mor', 'far', 'barn'] as const;

export type Xref01RelationCode = (typeof XREF01_RELATION_CODES)[number];

export const XREF01_RELATION_LABELS_DA: Record<Xref01RelationCode, string> = {
  mor: 'Mor',
  far: 'Far',
  barn: 'Barn',
  uspec_paaroerende: 'Uspecificeret pårørende el. værge',
};

export const WARNING_UNDER15_NO_RELATIVE =
  'Patienten er under 15 år og har ingen pårørende (mor, far, værge eller uspecificeret pårørende). Henvisningen kan ikke dannes, før en pårørende er tilføjet under Relationer.';

export const WARNING_UNDER15_CHOOSE_RELATIVE =
  'Patienten er under 15 år. Henvisningen skal indeholde en pårørende (mor, far, værge eller uspecificeret pårørende). Vælg hvem der medtages.';

export const WARNING_UNDER15_RELATIONS_ERROR =
  'Relationerne kunne ikke hentes. Henvisningen kan ikke dannes for en patient under 15 år, før det er bekræftet, at der er en pårørende (mor, far, værge eller uspecificeret pårørende).';

const ADDITIONAL_SET: ReadonlySet<string> = new Set(XREF01_ADDITIONAL_DIAGNOSE_DESCRIPTION_CODES);
const REFER_SET: ReadonlySet<string> = new Set(XREF01_REFER_DIAGNOSE_DESCRIPTION_CODES);
const RELATION_SET: ReadonlySet<string> = new Set(XREF01_RELATION_CODES);

export function isXref01AdditionalDiagnoseDescriptionCode(
  value: string | null | undefined,
): value is Xref01AdditionalDiagnoseDescriptionCode {
  return typeof value === 'string' && ADDITIONAL_SET.has(value);
}

export function isXref01ReferDiagnoseDescriptionCode(
  value: string | null | undefined,
): value is Xref01ReferDiagnoseDescriptionCode {
  return typeof value === 'string' && REFER_SET.has(value);
}

export function isXref01RelationCode(value: string | null | undefined): value is Xref01RelationCode {
  return typeof value === 'string' && RELATION_SET.has(value);
}

export function additionalDiagnoseDescriptionCodeOrDefault(
  value: string | null | undefined,
): Xref01AdditionalDiagnoseDescriptionCode {
  return isXref01AdditionalDiagnoseDescriptionCode(value)
    ? value
    : XREF01_ADDITIONAL_DIAGNOSE_DEFAULT;
}

export function referDiagnoseDescriptionCodeOrDefault(
  value: string | null | undefined,
): Xref01ReferDiagnoseDescriptionCode {
  return isXref01ReferDiagnoseDescriptionCode(value) ? value : XREF01_REFER_DIAGNOSE_DEFAULT;
}

/**
 * Refer DiagnoseDescriptionCode on the outgoing payload.
 * LPR viderehenvisning still wins (henv_diagnose_ny_sygdom / henv_diagnose_samme_sygdom).
 * Otherwise the clinician's selector (default henv_diagnose).
 */
export function referDiagnoseDescriptionCodeForRequest(
  forward: boolean,
  lprDiagnoseDescriptionCode: string,
  selected: string | null | undefined,
): Xref01ReferDiagnoseDescriptionCode {
  if (forward && isXref01ReferDiagnoseDescriptionCode(lprDiagnoseDescriptionCode)) {
    return lprDiagnoseDescriptionCode;
  }
  return referDiagnoseDescriptionCodeOrDefault(selected);
}
